import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * Admin scheduler/cron-jobs surface (ADR-0037 follow-up).
 *
 * Read-only view of the cron registry + the `cron_runs` audit log so
 * admins can answer "which jobs are armed, when did they last run, did
 * it succeed?" without shelling into the API box. POST `runNow` is
 * intentionally out of scope here - manual triggers stay scripted
 * until we can audit them as deliberate ops actions.
 */

export const CRON_RUN_STATUSES = ['RUNNING', 'SUCCESS', 'FAILED', 'SKIPPED'] as const;
export const CronRunStatusSchema = z.enum(CRON_RUN_STATUSES);
export type CronRunStatus = z.infer<typeof CronRunStatusSchema>;

/** A registered cron job + its current expression + a snapshot of its last run. */
export const SchedulerJobItemSchema = z.object({
  jobKey: z.string(),
  displayName: z.string(),
  description: z.string().nullable(),
  /** Active cron expression after env overrides (CRON_<JOBKEY>_EXPRESSION). */
  expression: z.string(),
  /** False when the master switch is off OR per-job env disable is set. */
  enabled: z.boolean(),
  /** Last `cron_runs` row for this jobKey - null when the job has never run. */
  lastRun: z
    .object({
      id: UuidSchema,
      status: CronRunStatusSchema,
      triggeredBy: z.string(),
      startedAt: IsoDateTimeSchema,
      finishedAt: IsoDateTimeSchema.nullable(),
      durationMs: z.number().int().nullable(),
      errorMessage: z.string().nullable(),
    })
    .nullable(),
});
export type SchedulerJobItem = z.infer<typeof SchedulerJobItemSchema>;

export const SchedulerJobsResponseSchema = z.object({
  jobs: z.array(SchedulerJobItemSchema),
});
export type SchedulerJobsResponse = z.infer<typeof SchedulerJobsResponseSchema>;

/** One audit row for a single cron tick. */
export const SchedulerRunItemSchema = z.object({
  id: UuidSchema,
  jobKey: z.string(),
  triggeredBy: z.string(),
  actorId: UuidSchema.nullable(),
  status: CronRunStatusSchema,
  startedAt: IsoDateTimeSchema,
  finishedAt: IsoDateTimeSchema.nullable(),
  /** Wall-clock duration in ms. NULL while the run is still in flight. */
  durationMs: z.number().int().nullable(),
  /** Job-specific summary payload (whatever `job.run()` returned). */
  summary: z.unknown(),
  errorMessage: z.string().nullable(),
});
export type SchedulerRunItem = z.infer<typeof SchedulerRunItemSchema>;

export const SchedulerRunsListQuerySchema = PaginationQuerySchema.extend({
  jobKey: z.string().optional(),
  status: CronRunStatusSchema.optional(),
});
export type SchedulerRunsListQuery = z.infer<typeof SchedulerRunsListQuerySchema>;

export const SchedulerRunsListResponseSchema = paginatedResponseSchema(SchedulerRunItemSchema);
export type SchedulerRunsListResponse = z.infer<typeof SchedulerRunsListResponseSchema>;
